import type React from "react";
import {Autocomplete, Button, Stack, TextField, Typography} from "@mui/material";
import {
    type IndexedCategory,
    type LevelSelection,
    levelOptions,
    selectLevel,
    type TaxonomyIndex,
} from "./taxonomyTreeIndex";

type TaxonomyLevelFilterProps = {
    index: TaxonomyIndex;
    /** Chosen category id per level, L1 first */
    selection: LevelSelection;
    onChange: (selection: LevelSelection) => void;
};

/**
 * One dropdown per level, shown above the canvas. An upper choice narrows the levels below it;
 * choosing a lower level leaves the upper ones as they are.
 */
export const TaxonomyLevelFilter: React.FC<TaxonomyLevelFilterProps> = ({
                                                                            index,
                                                                            selection,
                                                                            onChange,
                                                                        }) => {
    const parentLabel = (c: IndexedCategory) => c.parentId === null ? '' : index.byId.get(c.parentId)?.label ?? '';

    return (
        // Compact, like the value chain's supply type filter.
        <Stack
            direction="row"
            alignItems="center"
            gap="2px"
            useFlexGap
            flexWrap="wrap"
            sx={{p: '2px', bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.100', borderRadius: '8px'}}
        >
            {index.levels.map((_, levelIdx) => {
                const options = levelOptions(index, levelIdx, selection);
                const chosen = selection[levelIdx];
                const value = (chosen === undefined ? undefined : index.byId.get(chosen)) ?? null;
                return (
                    <Autocomplete<IndexedCategory>
                        key={levelIdx}
                        size="small"
                        sx={{width: 160}}
                        slotProps={{
                            // Wider and taller than the 160px field, so long category names fit.
                            popper: {placement: 'bottom', sx: {minWidth: 320}},
                            listbox: {
                                sx: {
                                    fontSize: 12.5,
                                    maxHeight: '60vh',
                                    // The parent category above each group: same size as the options, set apart by a pale background.
                                    '& .MuiAutocomplete-groupLabel': {
                                        fontSize: 12.5,
                                        fontWeight: 600,
                                        lineHeight: '28px',
                                        color: 'text.secondary',
                                        bgcolor: 'grey.50',
                                    },
                                },
                            },
                        }}
                        options={options}
                        value={value}
                        // Always group below L1: MUI breaks if `groupBy` toggles while it still renders the old options.
                        groupBy={levelIdx > 0 ? parentLabel : undefined}
                        getOptionLabel={c => c.label}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_, c) => onChange(selectLevel(index, selection, levelIdx, c?.id))}
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder="All"
                                aria-label={`L${levelIdx + 1}`}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        startAdornment: (
                                            <Typography fontSize={12} fontWeight={600} color="text.secondary" sx={{pl: 0.5}}>
                                                L{levelIdx + 1}
                                            </Typography>
                                        ),
                                    },
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: 30,
                                        py: 0,
                                        fontSize: 12.5,
                                        bgcolor: 'common.white',
                                        borderRadius: '8px',
                                    },
                                    '& .MuiInputBase-input::placeholder': {color: 'text.secondary', opacity: 1},
                                }}
                            />
                        )}
                    />
                );
            })}
            {selection.some(id => id !== undefined) && (
                <Button size="small" onClick={() => onChange([])} sx={{height: 30, fontSize: 12.5}}>Clear</Button>
            )}
        </Stack>
    );
};
